import { yupResolver } from '@hookform/resolvers/yup';
import { IFetchCountriesQuery } from 'api/country';
import { uploadFile } from 'api/file';
import { createPlan, useCountries } from 'api/plan';
import { useTemplatesList } from 'api/template';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ICountry, ITemplate, PlanPermission } from 'types/dto';
import { FEATURE_ADMIN_AREA, isEnabled } from 'utils/feature';
import * as yup from 'yup';

const MAX_TEMPLATE_FILE_SIZE_MB = 30;

interface IUploadForm {
  typeId: number;
  countryId: number;
  adminAreaId: number;
  files: FileList;
  permission: PlanPermission;
}

export interface IUseUploadFormOptions {
  onSuccessSubmit?: (plan: { id: number; country?: ICountry; adminArea?: ICountry; template?: ITemplate }) => void;
  onFailSubmit?: (err: any) => void;
}

export function useUploadForm({ onSuccessSubmit, onFailSubmit }: IUseUploadFormOptions = {}) {
  const { formatMessage } = useIntl();
  const uploadFormValidationSchema = useMemo(() => {
    const schema = yup
      .object({
        typeId: yup.string().required(formatMessage({ id: 'validation.required' })),
        countryId: yup.string().required(formatMessage({ id: 'validation.required' })),
        files: yup
          .mixed<FileList>()
          .test('requiredFile', formatMessage({ id: 'validation.required' }), files => files && files?.length > 0)
          .test(
            'fileSize',
            formatMessage({ id: 'validation.maxFileSize' }, { size: MAX_TEMPLATE_FILE_SIZE_MB }),
            files => {
              if (files && files.length) {
                return Array.from(files).every(file => file.size < Math.pow(1024, 2) * MAX_TEMPLATE_FILE_SIZE_MB);
              }
              return true;
            },
          ),
        permission: yup.string().required(formatMessage({ id: 'validation.required' })),
      })
      .required();

    if (isEnabled(FEATURE_ADMIN_AREA)) {
      schema.concat(
        yup.object({
          adminAreaId: yup.string().required(formatMessage({ id: 'validation.required' })),
        }),
      );
    }

    return schema;
  }, [formatMessage]);
  const { handleSubmit, ...form } = useForm<IUploadForm>({
    resolver: yupResolver(uploadFormValidationSchema),
  });
  const watchCountryId = form.watch('countryId');
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>();
  const { data: templates, isFetching: isTemplatesLoading } = useTemplatesList({
    cacheTime: Infinity,
    staleTime: Infinity,
  });
  const { data: countries, isFetching: isCountriesLoading } = useCountries(
    {
      sorting: ['name:asc'],
      adminLevel: 0,
    } as IFetchCountriesQuery,
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
  const { data: adminAreas, isFetching: isAdminAreasLoading } = useCountries(
    {
      sorting: ['name:asc'],
      parentId: watchCountryId,
    } as IFetchCountriesQuery,
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!watchCountryId,
    },
  );

  const onSubmit = handleSubmit(async (data: IUploadForm) => {
    try {
      const uploadedFiles = await uploadFile(data.files);
      const { id } = await createPlan({
        typeId: data.typeId,
        countryId: data.countryId,
        adminAreaId: data.adminAreaId > 0 ? data.adminAreaId : null,
        file: uploadedFiles[0],
        permission: data.permission,
      });
      onSuccessSubmit &&
        onSuccessSubmit({
          id,
          template: templates?.find(template => template.id === Number(data.typeId)),
          country: countries?.find(country => country.id === Number(data.countryId)),
          adminArea: adminAreas?.find(adminArea => adminArea.id === Number(data.adminAreaId)),
        });
    } catch (err: any) {
      setSubmitErrorMessage((err.response.data || err).message);
      onFailSubmit?.(err);
    }
  });

  const clearSubmitErrorMessage = () => {
    setSubmitErrorMessage('');
  };

  useEffect(() => {
    if (countries?.length === 1) {
      form.setValue('countryId', countries[0].id);
    }
    // eslint-disable-next-line
  }, [countries?.length, form.setValue]);

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if ((name === 'countryId' && type === 'change') || !value) {
        form.setValue('adminAreaId', -1);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return {
    isReady:
      !isTemplatesLoading &&
      !isCountriesLoading &&
      (isEnabled(FEATURE_ADMIN_AREA) ? !isAdminAreasLoading : true) &&
      !form.formState.isSubmitting,
    submitErrorMessage,
    templates,
    countries,
    adminAreas,
    isTemplatesLoading,
    isCountriesLoading,
    isAdminAreasLoading,
    watchCountryId,
    onSubmit,
    clearSubmitErrorMessage,
    ...form,
  };
}
